/**
 * PWA glue for the softphone: register the service worker, subscribe to Web Push
 * (so the phone rings when backgrounded), and relay the SW's notification taps
 * back to the softphone (focus-to-answer).
 */

/** Register the service worker (idempotent). Returns the registration or null. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

/** base64url VAPID key → ArrayBuffer for PushManager.subscribe. */
function urlBase64ToBuffer(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buf;
}

/**
 * Subscribe to Web Push and register the subscription with the backend for this
 * device. Best-effort — returns false if push is unavailable/denied/unconfigured.
 */
export async function subscribeToPush(
  apiBase: string,
  token: string,
  deviceId: string,
): Promise<boolean> {
  try {
    const reg = await navigator.serviceWorker.ready;
    if (!('pushManager' in reg)) return false;

    // Fetch the server VAPID public key.
    const keyRes = await fetch(`${apiBase}/api/push/vapid-public-key`);
    if (!keyRes.ok) return false; // push not configured server-side
    const { publicKey } = await keyRes.json();

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToBuffer(publicKey),
      });
    }

    const res = await fetch(`${apiBase}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ deviceId, subscription: sub.toJSON() }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Wire SW → softphone messages (notification tap "answer"/"decline"). */
export function onCallAction(handler: (action: string) => void): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('message', (evt) => {
    const data = evt.data as { type?: string; action?: string };
    if (data?.type === 'call_action') handler(data.action ?? 'answer');
  });
}

/**
 * Best-effort keep-alive so the softphone stays registered in the background as
 * long as the OS permits (an installed PWA on Android survives backgrounding far
 * better than a browser tab). None of this keeps a WebSocket open once the app is
 * fully swiped away — that's a native-only capability — but it maximizes the
 * window where inbound calls ring WITHOUT a slow cold-boot wake.
 *
 *  - Screen Wake Lock while foreground (re-acquired on visibility).
 *  - Periodic Background Sync registration (installed PWA): lets the SW wake the
 *    app periodically to re-register.
 *  - A `keepalive` heartbeat the caller can hook to re-register if the transport
 *    silently died.
 */
export function startKeepAlive(reRegister: () => void): void {
  let wakeLock: any = null;
  const acquireWakeLock = async (): Promise<void> => {
    try {
      if ('wakeLock' in navigator && document.visibilityState === 'visible') {
        wakeLock = await (navigator as any).wakeLock.request('screen');
        wakeLock.addEventListener?.('release', () => { wakeLock = null; });
      }
    } catch { /* not supported / denied */ }
  };
  void acquireWakeLock();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void acquireWakeLock();
      reRegister(); // coming back to foreground → make sure we're registered
    }
  });

  // Periodic Background Sync (installed PWA, Chromium): wake the SW ~every 15 min
  // to nudge a re-register. Requires permission; silently no-ops otherwise.
  (async () => {
    try {
      const reg: any = await navigator.serviceWorker.ready;
      if (reg && 'periodicSync' in reg) {
        const status = await (navigator as any).permissions?.query?.({ name: 'periodic-background-sync' });
        if (!status || status.state === 'granted') {
          await reg.periodicSync.register('uc-keepalive', { minInterval: 15 * 60 * 1000 });
        }
      }
    } catch { /* unsupported */ }
  })();

  // Foreground heartbeat: every 20s, ask the caller to verify/re-register.
  setInterval(() => { if (document.visibilityState === 'visible') reRegister(); }, 20000);

  // SW → app "re-register" nudge (from periodicSync or push).
  navigator.serviceWorker?.addEventListener('message', (evt) => {
    if ((evt.data as any)?.type === 'reregister') reRegister();
  });
}
